import moment from "moment";
import { isWeb } from "./platform";
import { USER_INFO } from "../constants/storage-keys";
import { GetLocalStorage } from "./cryptography";
import { ClinicMemberStatus, Gender, Modality } from "../constants/options";
import { UserInfo } from "../stores/user-store";
import { IsEmptyStr, IsZero } from "./field-validation";
import { CNDateTimeFormat } from "./date";
import i18next, { t } from "i18next";
import { MODALITY_OPTIONS } from "../data/booking";
import { GeneralModalityIcon } from "../images/svg-components";

export const IS_SMALL_SCREEN = isWeb ? window.innerWidth <= 1024 : false;
export const IS_MOBILE_SCREEN = isWeb ? window.innerWidth <= 768 : false;

export enum Language {
  ENGLISH = "en-AU",
  ZH_CN = "zh-CN",
}
export const LangList = [Language.ENGLISH, Language.ZH_CN];
export const GetLangLabel = (lang: Language) => {
  switch (lang) {
    case Language.ENGLISH:
      return "EN";
    case Language.ZH_CN:
      return "中文";
  }
};
// The active language is owned by i18next, which localization/i18n.ts resolves per-route. Deriving
// it from there (rather than reading localStorage directly) keeps every localised helper below -
// PickLocalised, NameConversion, GetModalityName - and the `lang=` API params in the stores in step
// with whatever the UI is actually showing.
export const GetLangKey = (): Language => {
  const lang = i18next.resolvedLanguage ?? i18next.language;
  return LangList.includes(lang as Language) ? (lang as Language) : Language.ENGLISH;
};

export const GetUserProfile = () => {
  let userInfo: UserInfo = {
    id: 0,
    username: "",
    firstName: "",
    lastName: "",
    templateId: 0,
    gender: Gender.MALE,
    email: "",
    availableClinics: [],
    currentClinicId: 0,
    isPublished: false,
    packagePermissions: {
      features: [],
      clinicPackages: [],
    },
  };
  if (isWeb && localStorage.USER_INFO) userInfo = GetLocalStorage(USER_INFO);
  return userInfo;
};

export const GetErrorInfo = (err: any) => {
  //err is err:obj
  console.log(err);
  if (err.message === "ERR_CANCELED") {
    // DO NOTHING, THIS IS A CANCELLED REQUEST
    return "";
  } else if (err.message.includes("Network error!")) {
    return "Failed to connect to server.";
  } else if (err.name === "AxiosError") {
    if (err.response.status === 404) return "404 Resource Not Found";
    else if (err.response.status === 400) return "Invalid request format";
  } else if (err.name === "TypeError") {
    return `Error: ${err.message}`;
  } else if (!err.response || !err.response.data.message)
    return "Unknown error for VAI Server. Please contact the administrator.";
  let input = err.response.data.message;
  const regex = /Error:/g;
  let match: RegExpExecArray | null;
  let lastMatchIndex = -1;
  while ((match = regex.exec(input)) !== null) {
    lastMatchIndex = match.index;
  }
  if (lastMatchIndex !== -1) {
    return input.substring(lastMatchIndex + "Error:".length).trim();
  }
  return "Unknown error for VAI Server. Please contact the administrator.";
};

export const TruncateText = (text: string, limit: number) => {
  let bytes = 0;
  let result = "";

  for (const char of text) {
    bytes += char.charCodeAt(0) > 255 ? 2 : 1;
    if (bytes > limit) break;
    result += char;
  }
  return bytes > limit ? result + "..." : result;
};

export const OnProcessObjList = (
  index: number,
  sourceList: any[],
  req?: any
): any[] => {
  let finalList = [...sourceList];
  if (index < 0) finalList.push(req);
  else finalList.splice(index, 1);
  return finalList;
};
export enum APILanguage {
  en = "en",
  cn = "cn",
}

export const ALPHABETS: string[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export const GetDeepCopy = (obj: any) => JSON.parse(JSON.stringify(obj));

export const IsWorkingInClinic = (clinicMemberStatus: ClinicMemberStatus) => {
  return (
    clinicMemberStatus == ClinicMemberStatus.APPROVED ||
    clinicMemberStatus == ClinicMemberStatus.PENDING_QUIT_APPROVAL
  );
};

export const IsClinicOwner = (clinic: any, userId: number | string | null | undefined): boolean => {
  if (!clinic || userId == null) return false;
  const targetUserId = String(userId);
  return String(clinic.ownerId) === targetUserId
    || (Array.isArray(clinic.allOwners)
      && clinic.allOwners.some((owner: any) => owner != null && String(owner.ownerId) === targetUserId));
};

export const NameConversion = (firstName: string, lastName: string) => {
  if (IsEmptyStr(firstName) || IsEmptyStr(lastName)) return "";
  return GetLangKey() === Language.ZH_CN
    ? `${lastName}${firstName}`
    : `${firstName} ${lastName}`;
};

export const DaysCountDown = (endTime: string) => {
  const now = moment();
  const end = moment(endTime, CNDateTimeFormat);
  const diff = end.diff(now, "days");
  return diff;
};

export const ReformatAuPhoneNum = (
  mobile: string,
  removeCountryCode: boolean = true
) => {
  if (!mobile) return mobile;
  const trimmed = mobile.trim();
  if (removeCountryCode) {
    // 如果是 +61 开头，去掉 +61，替换成 0
    if (trimmed.startsWith("+61")) {
      return "0" + trimmed.slice(3);
    }
    return trimmed;
  } else {
    if (trimmed.startsWith("0")) {
      return "+61" + trimmed.slice(1);
    }
    return trimmed;
  }
};

export const GetGenderLabel = (gender: Gender) => {
  if (gender == Gender.MALE) return t('MALE');
  if (gender == Gender.FEMALE) return t('FEMALE');
  if (gender == Gender.PREFER_NOT_TO_SAY) return t('PREFER_NOT_TO_SAY');
  return "-";
}

export const ConstructTabTitle = (title: string) => {
  return title + " | Healthnex";
};
export const SetTabTitle = (title: string) => {
  if (!isWeb) return;
  document.title = ConstructTabTitle(title);
};

export const PickLocalised = (item: any, baseField: string) => {
  const lang = GetLangKey();
  const en = item?.[`${baseField}En`] ?? "";
  const cn = item?.[`${baseField}Cn`] ?? "";
  if (lang == Language.ENGLISH) return en || cn;
  // if (lang == Language.TH_TH) return (item?.[`${baseField}Th`] ?? "") || en || cn;
  // if (lang == Language.VI_VN) return (item?.[`${baseField}Vn`] ?? "") || en || cn;
  return cn || en;
};


export const SortModalityList = (modalityList: any[]) => {
  const getId = (item: any) => {
    return item.modalityId ?? item.id;
  }
  let tcmModalities = [Modality.CHINESE_MEDICINE];
  let highPriorityModalities = [Modality.ACUPUNCTURE, Modality.MASSAGE, Modality.REMEDIAL_MASSAGE];
  let dynamicList = modalityList.filter((item: any) => !IsZero(item.clinicId));
  let tcmList = modalityList.filter((item: any) => tcmModalities.includes(getId(item)));
  let highPriorityList = modalityList.filter((item: any) => highPriorityModalities.includes(getId(item)));
  let remainedList = modalityList.filter((item: any) => !highPriorityModalities.includes(getId(item)) && !tcmModalities.includes(getId(item)) && IsZero(item.clinicId))
    .sort((a: any, b: any) => PickLocalised(a, "name").localeCompare(PickLocalised(b, 'name')));
  return [...tcmList, ...dynamicList, ...highPriorityList, ...remainedList];
}

export const GetModalityIcon = (modality: any) => {
  const getId = (item: any) => {
    return item.modalityId ?? item.id;
  }
  return !modality || !IsZero(modality.clinicId) ? GeneralModalityIcon
    : MODALITY_OPTIONS.find((option: any) => option.id === getId(modality))?.icon ?? GeneralModalityIcon;
}

export const GetModalityName = (modality: any) => {
  const lang = GetLangKey();
  const getId = (item: any) => {
    return item?.modalityId ?? item?.id;
  }
  if (!modality) return "";
  if (lang == Language.ENGLISH) return modality?.name;
  var name = IsEmptyStr(PickLocalised(modality, "name")) ? modality?.name : PickLocalised(modality, "name");
  return name + `${getId(modality) == Modality.REMEDIAL_MASSAGE ? " (Remedial Massage)" : ""}`;
}